/**
 * Document Center Page
 *
 * Central hub for generating essential dealer documents with smart-fill capability.
 */

import {
  BanknotesIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  PrinterIcon,
  ScaleIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import DocumentFormModal from '../../components/documents/DocumentFormModal';
import apiService from '../../services/api';

interface DocumentTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  required_fields: string[];
}

interface CustomerDocument {
  id: string;
  customer_id: string;
  created_by_user_id: string;
  template_id: string;
  template_name: string;
  file_name: string;
  file_path: string;
  mime_type: string;
  file_size: number;
  email_to?: string | null;
  emailed_at?: string | null;
  created_at: string;
}

interface CategoryInfo {
  name: string;
  icon: React.ReactNode;
  color: string;
}

const categories: Record<string, CategoryInfo> = {
  sales: {
    name: 'Sales Documents',
    icon: <ClipboardDocumentListIcon className="h-6 w-6" />,
    color: 'blue',
  },
  finance: {
    name: 'Finance Documents',
    icon: <BanknotesIcon className="h-6 w-6" />,
    color: 'green',
  },
  compliance: {
    name: 'Compliance Documents',
    icon: <ScaleIcon className="h-6 w-6" />,
    color: 'purple',
  },
  service: {
    name: 'Service Documents',
    icon: <UserIcon className="h-6 w-6" />,
    color: 'orange',
  },
};

const DocumentCenterPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const customerIdFilter = searchParams.get('customer_id');
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [customerDocuments, setCustomerDocuments] = useState<CustomerDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialData, setInitialData] = useState<Record<string, string>>({});

  const fetchTemplates = useCallback(async () => {
    try {
      const [templatesResponse, documentsResponse] = await Promise.all([
        apiService.get<{ templates: DocumentTemplate[] }>('/documents/templates'),
        customerIdFilter
          ? apiService.get<{ documents: CustomerDocument[] }>(
              `/documents/customers/${customerIdFilter}`
            )
          : Promise.resolve({ documents: [] }),
      ]);
      setTemplates(templatesResponse.templates);
      setCustomerDocuments(documentsResponse.documents || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load document templates');
    } finally {
      setLoading(false);
    }
  }, [customerIdFilter]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleGenerateDocument = async (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;

    setGenerating(templateId);

    try {
      const preview = await apiService.post<{ data?: Record<string, any> }>('/documents/preview', {
        template_id: templateId,
        custom_data: {},
      });
      const normalized: Record<string, string> = {};
      Object.entries(preview?.data || {}).forEach(([key, value]) => {
        normalized[key] = value === null || value === undefined ? '' : String(value);
      });

      setInitialData(normalized);
      setSelectedTemplate(template);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Failed to generate document:', error);
      toast.error('Failed to prepare document form');
    } finally {
      setGenerating(null);
    }
  };

  const handleFormSubmit = async (
    formData: Record<string, string>,
    action: 'download' | 'print' | 'email' = 'download',
    emailTo?: string
  ) => {
    if (!selectedTemplate) return;

    setGenerating(selectedTemplate.id);
    try {
      if (action === 'email' && emailTo) {
        await apiService.post('/documents/generate-and-email', {
          template_id: selectedTemplate.id,
          custom_data: formData,
          to_email: emailTo,
          subject: selectedTemplate.name,
        });
        toast.success(`Document emailed to ${emailTo}`);
        setIsModalOpen(false);
        return;
      }

      const response = await apiService.post(
        '/documents/generate',
        {
          template_id: selectedTemplate.id,
          custom_data: formData,
        },
        {
          responseType: 'blob',
        }
      );

      const blob = new Blob([response as BlobPart], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (action === 'print') {
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.addEventListener('load', () => printWindow.print());
        }
        toast.success('Print preview opened');
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${selectedTemplate.name.replace(/ /g, '_')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(`${selectedTemplate.name} generated successfully`);
      }

      window.URL.revokeObjectURL(url);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Failed to process document:', error);
      toast.error('Failed to process document');
    } finally {
      setGenerating(null);
    }
  };

  const getCategoryColor = (category: string) => {
    const color = categories[category]?.color || 'gray';
    return {
      border: `border-${color}-200`,
      bg: `bg-${color}-50`,
      text: `text-${color}-700`,
      hover: `hover:border-${color}-300`,
      icon: `text-${color}-600`,
    };
  };

  const filteredTemplates = selectedCategory
    ? templates.filter((t) => t.category === selectedCategory)
    : templates;

  const groupedTemplates = filteredTemplates.reduce(
    (acc, template) => {
      if (!acc[template.category]) {
        acc[template.category] = [];
      }
      acc[template.category].push(template);
      return acc;
    },
    {} as Record<string, DocumentTemplate[]>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading document templates...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Document Center</h1>
        <p className="mt-1 text-sm text-gray-600">
          {customerIdFilter
            ? 'Review saved documents for the selected customer and generate new dealer paperwork.'
            : 'Generate essential dealer documents with smart-fill from your inventory and customer data'}
        </p>
      </div>

      {customerIdFilter && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Saved Customer Documents</h2>
              <p className="text-sm text-gray-600">
                {customerDocuments.length} saved document{customerDocuments.length === 1 ? '' : 's'}{' '}
                linked to this customer.
              </p>
            </div>
          </div>

          {customerDocuments.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
              No saved customer documents were found yet. Generate a document below to start the
              record.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Document
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      File Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Created
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {customerDocuments.map((document) => (
                    <tr key={document.id}>
                      <td className="px-4 py-3 text-sm text-gray-900">{document.template_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{document.file_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(document.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() =>
                            window.open(
                              `/api/v1/documents/${document.id}/download`,
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }
                          className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-3 py-1.5 font-medium text-blue-700 hover:bg-blue-100"
                        >
                          <DocumentArrowDownIcon className="h-4 w-4" />
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            selectedCategory === null
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Documents ({templates.length})
        </button>
        {Object.entries(categories).map(([key, info]) => {
          const count = templates.filter((t) => t.category === key).length;
          if (count === 0) return null;

          return (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                selectedCategory === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {info.icon}
              <span>
                {info.name} ({count})
              </span>
            </button>
          );
        })}
      </div>

      {/* Document Templates Grid */}
      <div className="space-y-8">
        {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
          <div key={category}>
            <div className="flex items-center space-x-2 mb-4">
              <div
                className={`${categories[category]?.color ? `text-${categories[category].color}-600` : 'text-gray-600'}`}
              >
                {categories[category]?.icon}
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                {categories[category]?.name || category}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryTemplates.map((template) => {
                const colors = getCategoryColor(template.category);
                const isGenerating = generating === template.id;

                return (
                  <div
                    key={template.id}
                    className={`border-2 rounded-lg p-6 transition-all ${colors.border} ${colors.hover} hover:shadow-md bg-white`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-2 rounded-lg ${colors.bg}`}>
                        <DocumentTextIcon className={`h-6 w-6 ${colors.icon}`} />
                      </div>
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-full ${colors.bg} ${colors.text}`}
                      >
                        {template.category}
                      </span>
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {template.description}
                    </p>

                    {/* Required Fields */}
                    {template.required_fields.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-medium text-gray-700 mb-2">Required Fields:</p>
                        <div className="flex flex-wrap gap-1">
                          {template.required_fields.slice(0, 3).map((field) => (
                            <span
                              key={field}
                              className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                            >
                              {field.replace(/_/g, ' ')}
                            </span>
                          ))}
                          {template.required_fields.length > 3 && (
                            <span className="text-xs text-gray-500 px-2 py-1">
                              +{template.required_fields.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Generate Button */}
                    <button
                      onClick={() => handleGenerateDocument(template.id)}
                      disabled={isGenerating}
                      className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-md font-medium transition-colors ${
                        isGenerating
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {isGenerating ? (
                        <>
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <PrinterIcon className="h-4 w-4" />
                          <span>Edit & Generate</span>
                        </>
                      )}
                    </button>

                    <p className="text-xs text-gray-500 text-center mt-2">
                      💡 Use Smart Fill from detail pages
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {selectedCategory ? 'No templates in this category' : 'No document templates available'}
          </p>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircleIcon className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Quick Start Guide</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Generate blank documents from here</li>
              <li>
                • Use Smart Fill buttons on Vehicle/Customer/Deal pages for auto-populated documents
              </li>
              <li>• All documents are fillable PDFs</li>
              <li>• Documents are saved with tenant ID for record-keeping</li>
            </ul>
          </div>
        </div>
      </div>

      {selectedTemplate && (
        <DocumentFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleFormSubmit}
          template={selectedTemplate}
          initialData={initialData}
          generating={generating === selectedTemplate.id}
          showEmailOption={true}
        />
      )}
    </div>
  );
};

export default DocumentCenterPage;
